// Structured frontend logger — the canonical replacement for `console.*` per
// .claude/rules/logging-discipline.md.
//
// Two outputs. In dev it prints to the console. In every environment it
// hands `warning` and `error` lines to a SINK, when one is registered —
// `lib/observability.ts` registers the PostHog one at startup (PRD-57 FR-14).
// Until 2026-09 the production path was a bare `return`: 72 well-named
// `error`/`warning` call sites were writing into the void.
//
// The sink is injected rather than imported because `lib/analytics.ts` logs
// its own failures through this module; importing it back would be a cycle.
// `info` is never forwarded: those 96 call sites shadow the explicit
// analytics events and would only spend quota restating them.

type Context = Record<string, unknown> | undefined
type SinkLevel = "warning" | "error"
export type LogSink = (level: SinkLevel, event: string, context: Context) => void

let sink: LogSink | null = null

export function setLogSink(next: LogSink | null) {
  sink = next
}

function emit(level: "info" | "warning" | "error", event: string, context: Context) {
  if (level !== "info" && sink) {
    try {
      sink(level, event, context)
    } catch {
      // A sink failure has nowhere left to be reported — logging it would
      // re-enter the sink. Dropping it is the only safe option.
    }
  }
  if (!import.meta.env.DEV) return
  // This module is the only sanctioned boundary for direct `console.*` use
  // (logging-discipline.md). When `no-console` lands in eslint, this file
  // gets the override.
  const fn = level === "error" ? console.error : level === "warning" ? console.warn : console.log
  fn(`[${level}] ${event}`, context ?? {})
}

export const logger = {
  info: (event: string, context?: Context) => emit("info", event, context),
  warning: (event: string, context?: Context) => emit("warning", event, context),
  error: (event: string, context?: Context) => emit("error", event, context),
}
